<template>
  <div>
    <label v-if="label" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
      {{ label }}
    </label>

    <!-- Preview when proof exists -->
    <div v-if="previewUrl" class="mb-3 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0">
          <img :src="previewUrl" alt="Proof preview"
            class="w-24 h-24 object-cover rounded-lg shadow-md ring-2 ring-white dark:ring-slate-700 cursor-pointer"
            @click="$emit('view-proof', previewUrl)" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1">
              <p class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {{ isNewFile ? 'New Preview' : 'Current Proof' }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{ isNewFile ? 'Image ready to upload' : 'Existing payment proof' }}
              </p>
              <div v-if="isNewFile" class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 rounded-md text-xs font-medium">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Compressed & Ready
              </div>
              <div v-else class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 rounded-md text-xs font-medium">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                </svg>
                Saved Proof
              </div>
            </div>
            <button type="button" @click="removeProof"
              class="flex items-center justify-center w-8 h-8 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload area when no proof -->
    <div v-else class="relative">
      <input
        type="file"
        @change="handleFileChange"
        accept="image/*"
        :id="inputId"
        class="hidden"
      />
      <label :for="inputId"
        class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl cursor-pointer bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
        <div class="flex flex-col items-center justify-center pt-5 pb-6">
          <svg class="w-8 h-8 mb-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
          </svg>
          <p class="mb-1 text-sm text-slate-600 dark:text-slate-300 font-medium">
            <span class="text-indigo-600 dark:text-indigo-400">Click to upload</span> or drag and drop
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400">PNG, JPG, JPEG (Auto-compressed)</p>
        </div>
      </label>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import imageCompression from 'browser-image-compression'

const props = defineProps({
  modelValue: {
    type: File,
    default: null
  },
  existingProofUrl: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: 'Payment Proof (Optional)'
  },
  inputId: {
    type: String,
    default: 'proof-input'
  }
})

const emit = defineEmits(['update:modelValue', 'update:previewUrl', 'view-proof'])

const previewUrl = ref(props.existingProofUrl)
const isNewFile = ref(false)

// Watch for changes in existingProofUrl (when opening edit modal with existing proof)
watch(() => props.existingProofUrl, (newUrl) => {
  if (newUrl) {
    previewUrl.value = newUrl
    isNewFile.value = false
  }
})

async function compressImage(file) {
  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1024,
    useWebWorker: false,
  }
  try {
    return await imageCompression(file, options)
  } catch (error) {
    console.error('Error compressing image:', error)
    return file
  }
}

async function handleFileChange(event) {
  const file = event.target.files[0]
  if (file) {
    previewUrl.value = URL.createObjectURL(file)
    isNewFile.value = true
    const compressedFile = await compressImage(file)
    emit('update:modelValue', compressedFile)
    emit('update:previewUrl', previewUrl.value)
  }
}

function removeProof() {
  previewUrl.value = null
  isNewFile.value = false
  emit('update:modelValue', null)
  emit('update:previewUrl', null)
}
</script>
